/**
 * Shapes returned by `GET /briefs/:briefId/hybrid-reporting/{structure,metrics,timeseries}`.
 * Mirrors `UniCreate-ligher-API/src/hybrid-reporting/hybrid-reporting.types.ts`.
 */

export type HybridPlatform = 'tiktok' | 'instagram' | 'youtube' | 'snapchat';
export type MetricSource = 'ultra' | 'super';
export type SourceStatus = 'ok' | 'error' | 'skipped' | 'unavailable';

export const METRIC_KEYS = ['views', 'likes', 'comments', 'shares', 'saves'] as const;
export type MetricKey = (typeof METRIC_KEYS)[number];
/** `null` = no source knows the number. Never render it as 0. */
export type MetricValues = Record<MetricKey, number | null>;
export type MetricSources = Record<MetricKey, MetricSource | null>;

export type HybridCreator = { id: string; applicationId: string; name: string; avatarUrl: string | null };

export type HybridPost = {
  id: string;
  creatorId: string;
  applicationId: string | null;
  socialChannel: number;
  platform: HybridPlatform | null;
  deliverableLabel: string;
  url: string | null;
  postedAt: string | null;
  thumbnailUrl: string | null;
  videoId: string | null;
  adCode: string | null;
};

export type HybridNotPosted = {
  applicationId: string;
  creatorId: string;
  socialChannel: number;
  platform: HybridPlatform | null;
  deliverableLabel: string;
  videoApproved: boolean;
  nudgeAvailableAt: string | null;
};

export type HybridStructure = {
  brief: {
    id: string;
    title: string;
    kind: 'pieces' | 'legacy';
    currencyCode: string;
    socialChannels: number[];
    startDate: string | null;
    endDate: string | null;
    creatorsHired: number;
    postsExpected: number;
  };
  creators: HybridCreator[];
  posts: HybridPost[];
  notPosted: HybridNotPosted[];
  cost: { paidForLivePosts: number; currencyCode: string } | null;
  refresh: { lastRefreshedAt: string | null; nextAllowedAt: string | null };
  viewer: { canSeeCost: boolean; refreshInterval: 'hourly' | 'daily' };
};

export type HybridPostMetrics = {
  metrics: MetricValues;
  sources: MetricSources;
  source: MetricSource | null;
  caption: string | null;
  hashtags: string[];
  disclosureFound: boolean | null;
  followers: number | null;
  usualEngagementRate: number | null;
  postedAt: string | null;
  refreshedAt: string | null;
};

export type HybridMetrics = {
  posts: Record<string, HybridPostMetrics>;
  sources: { ultra: SourceStatus; super: SourceStatus };
  fetchedAt: string;
};

export type DailyPoint = { date: string } & MetricValues;

export type HybridTimeseries = {
  posts: Record<string, DailyPoint[]>;
  sources: { ultra: SourceStatus; super: SourceStatus };
  fetchedAt: string;
};

export type RefreshResponse = {
  refreshedAt: string;
  nextAllowedAt: string;
  taskNames: string[];
  posts: number;
  creatorAccounts: number;
  ultraRefreshesQueued: number;
  superRefreshesQueued: number;
};

export type NudgeResponse = { sent: boolean; creatorId: string; nextAvailableAt: string };

/** The eight summary tiles. `engagements` = likes + comments + shares + saves. */
export type ReportMetric = 'views' | 'engagements' | 'er' | 'likes' | 'comments' | 'shares' | 'saves' | 'cpm';
export type ReportSort = Exclude<ReportMetric, 'cpm'> | 'posted';
export type ReportRange = '7' | 'all';

/** Persisted per user in localStorage. Search and the open drawer are not. */
export type ReportPrefs = {
  metric: ReportMetric;
  sort: ReportSort;
  range: ReportRange;
  platform: HybridPlatform | 'all';
  showNotPosted: boolean;
  collapsed: boolean;
};

/** A live post joined with its stats, ready for the table and drawer. */
export type ReportRow = {
  post: HybridPost;
  creator: HybridCreator | null;
  stats: HybridPostMetrics | null;
  views: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  saves: number | null;
  engagements: number | null;
  er: number | null;
  postedAt: string | null;
};

export type ReportTotals = {
  n: number;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  saves: number;
  engagements: number;
  /** Percent. */
  er: number;
};
